import { ModelFactory } from "./../../shared/models/model-factory";
import { Injectable } from "@angular/core";
import {
  BestellingBewerkingDto,
  BestellingDorpelDto,
  BestellingDto,
  BestellingNeutDto,
  BestellingSluitpotDto,
} from "src/app/shared/api-models/models";
import { DorpelType } from "src/app/shared/models/dorpel-type.model";
import { NeutModel, NeutUtils } from "src/app/shared/models/neut.model";
import { SluitpotModel } from "src/app/shared/models/sluitpot.model";

const HOME_PATH_STORAGE_KEY = "hp";

@Injectable({
  providedIn: "root",
})
export class DataStateService {
  public homePath: string;
  public landingPagePath: string = "/";
  private submitModel: BestellingDto;
  private index: number = 0;

  constructor() {}

  public createDorpel(model: BestellingDorpelDto): DataStateService {
    const dorpelModel = model;
    const sm = this.getModel();
    sm.dorpels.push(dorpelModel);
    this.index = sm.dorpels.length - 1;

    return this;
  }

  public editDorpel(guid: string): DataStateService {
    this.index = this.getModel().dorpels.findIndex(
      (dorpel) => dorpel.guid === guid
    );
    return this;
  }

  public createTempDorpel(dorpelDto: BestellingDorpelDto): BestellingDorpelDto {
    const dorpel = JSON.stringify(dorpelDto);
    const temp = JSON.parse(dorpel) as BestellingDorpelDto;
    return temp;
  }

  public addDorpelData(
    dorpelType: DorpelType,
    dorpelOrder?: BestellingDorpelDto
  ): DataStateService {
    const dorpel = dorpelOrder || this.getCurrentDorpel();
    dorpel.aantalNeuten = dorpelType.aantalNeuten;
    dorpel.aantalSluitpotten = dorpelType.aantalSluitpotten;
    dorpel.dorpelCode = dorpelType.dorpel;
    dorpel.merk = dorpelType.merk;
    dorpel.zaagmaat = dorpelType.zaagmaat;
    dorpel.schema = dorpelType.schema;
    dorpel.firstMaatA = dorpelType.firstMaatA || 0;

    dorpel.deurmaatA = dorpelType.deurmaatA || 0;
    dorpel.deurmaatB = dorpelType.deurmaatB || 0;
    dorpel.deurmaatC = dorpelType.deurmaatC || 0;
    dorpel.maatA = dorpelType.maatA || 0;
    dorpel.maatB = dorpelType.maatB || 0;
    dorpel.maatC = dorpelType.maatC || 0;
    dorpel.maatZ = dorpelType.maatZ || 0;
    //opmerking detached to separate form handled in the dorpel page component
    //due to its position in the view - see addDorpelOpmerking method
    dorpel.aantal = dorpelType.aantal;
    dorpel.isInHouseDorpel = true;

    return this;
  }

  public addNeutenData(
    neutenModels: NeutModel[],
    dorpelOrder?: BestellingDorpelDto
  ): DataStateService {
    const dorpel = dorpelOrder || this.getCurrentDorpel();
    dorpel.neuten = neutenModels.map(
      (nm) =>
        <BestellingNeutDto>{
          daagmaat: nm.daagmaat,
          neutCode: nm.neut,
          neutDescription: nm.omschrijving,
          positie: NeutUtils.getPositionString(nm.position),
          sponningmaat: nm.sponningmaat,
          sponningsoort: nm.sponningsoort,
          glaslat: nm.glaslat,
          beglazingsprofiel: nm.beglazingsprofiel,
          aanslagprofiel: nm.aanslagprofiel,
          damverbreeding: nm.damverbreeding,
          gemonteerd: nm.gemonteerd,
          dubbelAanslagprofiel: nm.dubbelAanslagprofiel,
          linksVoorsponning: nm.linksVoorsponning,
          rechtsVoorsponning: nm.rechtsVoorsponning,
          isInHouseNeut: true,
          customWidth: nm.customWidth
        }
    );

    /**
     * for sliding doors, the maatA and maatZ values are already set by addDorpelData() function,
     * as they reside on the dorpel component / form (we use a special different layout);
     **/
    dorpel.maatA = dorpel.maatA || neutenModels[0].maatA || 0;
    dorpel.maatZ = dorpel.maatZ || neutenModels[neutenModels.length - 1].maatZ || 0;

    return this;
  }

  public addSluitpottenData(
    models: SluitpotModel[],
    dorpelOrder?: BestellingDorpelDto
  ): DataStateService {
    const dorpel = dorpelOrder || this.getCurrentDorpel();
    dorpel.sluitpotten = models.map(
      (model) =>
        <BestellingSluitpotDto>{
          maat: model.maat,
          maat2: model.maat2,
          sluitpotCode: model.sluitpotCode,
          omschrijving: model.omschrijving,
        }
    );

    return this;
  }

  public addBewerkingen(
    models: BestellingBewerkingDto[],
    dorpelOrder?: BestellingDorpelDto
  ): DataStateService {
    const dorpel = dorpelOrder || this.getCurrentDorpel();
    dorpel.bewerkingen = models;
    return this;
  }

  public addDorpelOpmerking(
    opmerking: string,
    dorpelOrder?: BestellingDorpelDto
  ): DataStateService {
    const dorpel = dorpelOrder || this.getCurrentDorpel();
    dorpel.opmerking = opmerking.trim();
    return this;
  }

  public addDrawing(
    drawingData?: string,
    dorpelOrder?: BestellingDorpelDto
  ): DataStateService {
    if (!drawingData) {
      return this;
    }

    const dorpel = dorpelOrder || this.getCurrentDorpel();
    dorpel.drawing = drawingData;
    return this;
  }

  public getModel(): BestellingDto {
    if (!this.submitModel) {
      this.submitModel = ModelFactory.createBestelling();
      this.submitModel.dorpels = [];
      this.submitModel.files = [];
    }

    if (!this.submitModel.files) {
      //prefilled order models come from BE without the files key
      this.submitModel.files = [];
    }

    return this.submitModel;
  }

  public setModel(model: BestellingDto): void {
    this.submitModel = model;
    this.index = 0;
  }

  public setHomePath(path: string): void {
    this.homePath = path;
  }

  public setLandingPagePath(path: string): void {
    this.landingPagePath = path;
  }

  public getCurrentDorpel(): BestellingDorpelDto {
    return this.getModel().dorpels[this.index];
  }

  public getDorpelById(dorpelId: string): BestellingDorpelDto {
    return this.getModel().dorpels.find((dorpel) => dorpel.guid === dorpelId);
  }
}
